<template>
  <div>
    <ResultsImport
      v-if="importing"
      :catalog-id="catalogId"
      @close="importing = false"
      @imported="onImported"
    />
    <div v-else>
      <div class="d-flex align-items-center flex-wrap p-1 m-1">
        <button
          type="button"
          class="view-toggle-btn mr-2"
          :class="{ active: view === 'participant' }"
          @click="view = 'participant'"
        >
          {{ $t('shop.results.participantView') }}
        </button>
        <button
          type="button"
          class="view-toggle-btn"
          :class="{ active: view === 'institution' }"
          @click="view = 'institution'"
        >
          {{ $t('shop.results.institutionView') }}
        </button>
      </div>

      <div class="d-flex align-items-center flex-wrap m-2">
        <SearchBox
          v-model="search"
          class="mr-3 mb-1"
          lazy
          constant-width
        />
        <template v-if="view === 'participant'">
          <FilterSelectDropdown
            class="mr-3 mb-1"
            :label="$t('shop.results.groups')"
            :all-label="$t('shop.results.allOption')"
            :selected.sync="groupCode"
            :options="groupOptions"
          />
          <FilterSelectDropdown
            class="mr-3 mb-1"
            :label="$t('shop.results.offer')"
            :all-label="$t('shop.results.allOption')"
            :selected.sync="offerId"
            :options="disciplineOptions"
          />
          <FilterSelectDropdown
            class="mr-3 mb-1"
            :label="$t('shop.results.gender')"
            :all-label="$t('shop.results.allOption')"
            :selected.sync="gender"
            :options="genderOptions"
          />
          <FilterSelectDropdown
            class="mr-3 mb-1"
            :label="$t('shop.results.ageGroup')"
            :all-label="$t('shop.results.allOption')"
            :selected.sync="ageGroupCode"
            :options="ageGroupOptions"
          />
        </template>

        <div class="flex-grow-1" />

        <button
          class="btn btn-sm btn-primary mb-1 mr-2"
          @click="importing = true"
        >
          <i class="fas fa-upload pr-1" />
          {{ $t('shop.results.import') }}
        </button>

        <button
          class="btn btn-sm btn-primary mb-1"
          :disabled="exportPending"
          @click="exportResults"
        >
          <Loader
            v-if="exportPending"
            size="14px"
            style="display: inline-block; margin-right: 6px"
          />
          <i
            v-else
            class="fas fa-download pr-1"
          />
          {{ $t('shop.results.download') }}
        </button>
      </div>

      <div class="results-table-wrapper mx-2">
        <table
          v-if="view === 'participant'"
          class="table table-sm results-table"
        >
          <thead>
            <tr>
              <th>{{ $t('shop.results.name') }}</th>
              <th>{{ $t('shop.results.groupColumn') }}</th>
              <th>{{ $t('shop.results.offerColumn') }}</th>
              <th>{{ $t('shop.results.genderColumn') }}</th>
              <th>{{ $t('shop.results.ageGroupColumn') }}</th>
              <th>{{ $t('shop.results.score') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in results"
              :key="i"
            >
              <td>{{ row.name }}</td>
              <td>{{ row.team }}</td>
              <td>{{ row.disciplineName }}</td>
              <td>{{ row.gender }}</td>
              <td>{{ row.ageGroupName }}</td>
              <td>{{ row.scoreDisplay }}</td>
            </tr>
          </tbody>
        </table>

        <table
          v-else
          class="table table-sm results-table institution-table"
        >
          <thead>
            <tr>
              <th>{{ $t('shop.results.groupColumn') }}</th>
              <th>{{ $t('shop.results.score') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in institutionResults"
              :key="i"
            >
              <td>{{ row.name }}</td>
              <td>{{ row.scoreDisplay }}</td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="pending"
          class="text-center py-4"
        >
          <Loader />
        </div>
        <div
          v-else-if="view === 'participant' ? results.length === 0 : institutionResults.length === 0"
          class="text-center text-secondary py-4"
        >
          {{ $t('shop.results.noResults') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FilterSelectDropdown from '@/components/utils/FilterSelectDropdown';
import SearchBox from '@/components/utils/SearchBox';
import getFileName from '@/utils/file-name';
import { mapActions, mapGetters } from 'vuex';
import ResultsImport from './ResultsImport';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    view: 'participant',
    search: '',
    groupCode: '',
    offerId: '',
    gender: '',
    ageGroupCode: '',
    pending: false,
    exportPending: false,
    importing: false,
  }),
  components: {
    SearchBox,
    FilterSelectDropdown,
    ResultsImport,
  },
  computed: {
    ...mapGetters('shop', [
      'results',
      'resultsFilters',
    ]),
    groupOptions() {
      return this.resultsFilters.groupOptions.map((x) => ({ value: x.value, text: x.label }));
    },
    disciplineOptions() {
      return this.resultsFilters.disciplineOptions.map((x) => ({ value: x.id, text: x.name }));
    },
    genderOptions() {
      return this.resultsFilters.genderOptions.map((x) => ({ value: x.value, text: x.label }));
    },
    ageGroupOptions() {
      return this.resultsFilters.ageGroupOptions.map((x) => ({ value: x.value, text: x.label }));
    },
    // Institution totals: every scored row (individual results, and the
    // per-team rows recorded for grouped disciplines like volleyball or
    // soccer) already carries its payer's/team's institution in `team`, so
    // the leaderboard is just those scores summed per institution. Time-based
    // disciplines (raw score in milliseconds) are excluded - summing those
    // together with point scores wouldn't mean anything. The row model has no
    // explicit score-type field, but FormatScore on the backend always
    // renders points as "<n> pkt" and time as "HH:mm:ss", so that suffix is a
    // reliable way to tell them apart.
    institutionResults() {
      const totals = new Map();

      this.results.forEach((row) => {
        if (!row.team || !row.scoreDisplay?.endsWith(' pkt')) return;
        totals.set(row.team, (totals.get(row.team) || 0) + (row.score || 0));
      });

      const search = this.search.trim().toLowerCase();

      return Array.from(totals.entries())
        .map(([name, score]) => ({ name, score, scoreDisplay: `${score} pkt` }))
        .filter((x) => !search || x.name.toLowerCase().includes(search))
        .sort((a, b) => b.score - a.score);
    },
  },
  watch: {
    view() {
      this.request();
    },
    search() {
      if (this.view === 'participant') {
        this.request();
      }
    },
    groupCode() {
      this.request();
    },
    offerId() {
      this.request();
    },
    gender() {
      this.request();
    },
    ageGroupCode() {
      this.request();
    },
  },
  methods: {
    ...mapActions('shop', [
      'getTournamentResults',
      'getTournamentResultsFilters',
      'exportTournamentResults',
    ]),
    query() {
      // Institution view aggregates across the whole catalog client-side
      // (see institutionResults) and its own search box filters that
      // aggregate by institution name, so the participant-level filters and
      // search don't apply to the request here.
      if (this.view === 'institution') {
        return {};
      }

      return {
        groupCode: this.groupCode || undefined,
        offerId: this.offerId || undefined,
        gender: this.gender || undefined,
        ageGroupCode: this.ageGroupCode || undefined,
        search: this.search || undefined,
      };
    },
    request() {
      this.pending = true;
      this.getTournamentResults({
        params: {
          catalogId: this.catalogId,
          query: this.query(),
        },
      })
        .finally(() => {
          this.pending = false;
        });
    },
    requestFilters() {
      this.getTournamentResultsFilters({
        params: {
          catalogId: this.catalogId,
        },
      });
    },
    exportResults() {
      this.exportPending = true;
      this.exportTournamentResults({
        params: {
          catalogId: this.catalogId,
          query: this.query(),
        },
      })
        .then(({ data, headers }) => {
          const name = getFileName(headers);
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', name);
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          this.exportPending = false;
        });
    },
    onImported() {
      this.importing = false;
      this.request();
    },
  },
  created() {
    this.$emit('page', 'results');
    this.requestFilters();
    this.request();
  },
};
</script>

<style lang="scss" scoped>
.view-toggle-btn {
  width: 105px;
  height: 31px;
  padding: 4px 15px;
  gap: 5px;
  border-radius: 8px;
  border: 1px solid var(--Grey, #BCBCBC);
  background: #FFFFFF;
  color: #4E5056;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;

  &.active {
    background: var(--Pagaspot-Light-blue, #E5F0FE);
    border: 1px solid var(--Pagaspot-Dark-blue, #0057C6);
    color: var(--Pagaspot-Dark-blue, #0057C6);
  }
}

.results-table-wrapper {
  height: calc(100vh - 300px);
  overflow-x: auto;
  overflow-y: auto;
}

.institution-table {
  th,
  td {
    &:first-child {
      width: 460px;
    }
  }
}

.results-table {
  th {
    font-size: 0.7rem;
    color: #888;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
  }
}
</style>
